import React, { useState } from 'react';
import { Nav, Tab, Stack, Row, Col, Button, Alert } from 'react-bootstrap';
import { IMessageDetails } from '../../models/MessageDetails';
import { ServiceManager } from '../../services/ServiceManager';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLightbulb, faExclamationTriangle, faShare, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

interface IMessageDetailsModalProps {
    messageDetails: IMessageDetails;
    onClose: () => void;
}

export const MessageDetailsModal: React.FC<IMessageDetailsModalProps> = ({ messageDetails, onClose }) => {
    const [activeTab, setActiveTab] = useState('insights');

    const copyToClipboard = async () => {
        try {
            const url = window.location.href.replace(/&messageId=[^&]+/i, '') + `&messageId=${messageDetails.id}`;
            await navigator.clipboard.writeText(url);
            ServiceManager.ToastServer.showSuccess("The URL has been created and copied to clipboard.");
        } catch (error) {
            ServiceManager.ToastServer.showError("There was some issue while copying the URL to clipboard.");
        }
    };

    return (
        <div className="message-details-modal">
            <Tab.Container activeKey={activeTab} onSelect={(k) => k && setActiveTab(k)}>
                <Nav variant="tabs" className="message-details-tabs" role="tablist">
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="insights" 
                            role="tab"
                            aria-selected={activeTab === 'insights'}
                            aria-controls="insights-tab"
                        >
                            Insights
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="stackTrace" 
                            role="tab"
                            aria-selected={activeTab === 'stackTrace'}
                            aria-controls="stackTrace-tab"
                        >
                            Stack Trace
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="score"
                            role="tab"
                            aria-selected={activeTab === 'score'}
                            aria-controls="score-tab"
                        >
                            Score
                        </Nav.Link>
                    </Nav.Item>
                    <Nav.Item>
                        <Nav.Link 
                            eventKey="escalation"
                            role="tab"
                            aria-selected={activeTab === 'escalation'}
                            aria-controls="escalation-tab"
                        >
                            Escalation
                        </Nav.Link>
                    </Nav.Item>
                </Nav>

                <Tab.Content className="message-details-content">
                    {/* Insights Tab */}
                    <Tab.Pane eventKey="insights" id="insights-tab" role="tabpanel" aria-labelledby="insights">
                        <InsightsTab insights={messageDetails.insights} message={messageDetails.message} />
                    </Tab.Pane>
                    
                    {/* Stack Trace Tab */}
                    <Tab.Pane eventKey="stackTrace" id="stackTrace-tab" role="tabpanel" aria-labelledby="stackTrace">
                        <StackTraceTab stackTrace={messageDetails.stackTrace} />
                    </Tab.Pane>

                    {/* Score Tab */}
                    <Tab.Pane eventKey="score" id="score-tab" role="tabpanel" aria-labelledby="score">
                        <ScoreTab score={messageDetails.score} />
                    </Tab.Pane>

                    {/* Escalation Tab */}
                    <Tab.Pane eventKey="escalation" id="escalation-tab" role="tabpanel" aria-labelledby="escalation">
                        <EscalationTab escalation={messageDetails.escalation} />
                    </Tab.Pane>
                </Tab.Content>
            </Tab.Container>

            <Row className="message-details-footer">
                <Col>
                    <Button variant="light" className="w-100" onClick={onClose}>
                        Close
                    </Button>
                </Col>
                <Col>
                    <Button variant="primary" className="w-100 crbc-bg-color" onClick={copyToClipboard}>
                        <FontAwesomeIcon icon={faShare} className="me-2" />
                        Share
                    </Button>
                </Col>
            </Row>
        </div>
    );
};

interface InsightsTabProps {
    insights?: IMessageDetails['insights'];
    message: string;
}

const InsightsTab: React.FC<InsightsTabProps> = ({ insights, message }) => {
    if (!insights) {
        return (
            <div className="no-data">
                <FontAwesomeIcon icon={faInfoCircle} className="no-data-icon" />
                <p className="no-data-text">No insights available</p>
            </div>
        );
    }

    return (
        <Stack className="insights-tab mt-4">
            <div className="insights-section">
                <h3 className="section-title">Message</h3>
                <div className="message-box mt-3">
                    <span>{message}</span>
                </div>
            </div>
            <div className="insights-section">
                <h3 className="section-title">AI Translation</h3>
                <p className="ai-translation">{insights.aiTranslation}</p>
            </div>

            {insights.recommendations && insights.recommendations.length > 0 && (
                <div className="recommendations-section">
                    <div className="recommendations-header">
                        <FontAwesomeIcon icon={faLightbulb} className="recommendations-icon" />
                        <h3 className="section-title">Recommendations</h3>
                    </div>
                    <ul className="recommendations-list border border-1">
                        {insights.recommendations.map((recommendation, index) => (
                            <li key={index} className="recommendation-item">
                                {recommendation}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </Stack>
    );
};

interface StackTraceTabProps {
    stackTrace?: string;
}

const StackTraceTab: React.FC<StackTraceTabProps> = ({ stackTrace }) => {
    if (!stackTrace) {
        return (
            <div className="no-data">
                <FontAwesomeIcon icon={faInfoCircle} className="no-data-icon" />
                <p className="no-data-text">No stack trace available</p>
            </div>
        );
    }

    return (
        <Stack className="insights-tab mt-4">
            <div className="insights-section">
                <div className="stack-trace-box" tabIndex={0}>
                    <span>{stackTrace}</span>
                </div>
            </div>
        </Stack>
    );
};

interface ScoreTabProps {
    score?: IMessageDetails['score'];
}

const ScoreTab: React.FC<ScoreTabProps> = ({ score }) => {
    if (!score) {
        return (
            <div className="no-data">
                <FontAwesomeIcon icon={faInfoCircle} className="no-data-icon" />
                <p className="no-data-text">No score information available</p>
            </div>
        );
    }

    return (
        <Stack gap={3} className="score-tab mt-4">
            <div className="score-header">
                <svg className="score-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <h3 className="section-title mb-0">Similarity Score</h3>
            </div>
            <div className="similarity-score-section border border-1 mb-3">                
                <div className="score-value">
                    <span className="score-percentage">{score.similarityScore}%</span>
                </div>
                
                <p className="score-description">
                    This message has a {score.similarityScore}% similarity score with messages in our database.
                </p>

                <div className="progress similarity-progress-bar">
                    <div
                        role="progressbar"
                        className="progress-bar"
                        aria-valuenow={score.similarityScore}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        title={`Similarity score: ${score.similarityScore}%`}
                        style={{ width: `${score.similarityScore}%` }}
                    />
                </div>
            </div>

            {score.isFlaggedForReview && (
                <Alert variant="warning" className="flagged-alert">
                    <div className="flagged-content">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="flag-icon" />
                        <div className="flagged-text-content">
                            <div className="flagged-title">Flagged for Human Review</div>
                            {score.flagReason && (
                                <p className="flag-reason">{score.flagReason}</p>
                            )}
                        </div>
                    </div>
                </Alert>
            )}
        </Stack>
    );
};

interface EscalationTabProps {
    escalation?: IMessageDetails['escalation'];
}

const EscalationTab: React.FC<EscalationTabProps> = ({ escalation }) => {
    if (!escalation) {
        return (
            <div className="no-data">
                <FontAwesomeIcon icon={faInfoCircle} className="no-data-icon" />
                <p className="no-data-text">No escalation information available</p>
            </div>
        );
    }

    return (
        <Stack gap={3} className="escalation-tab mt-4 mb-3">            
            <div className="escalation-section">
                <div className="escalation-info">
                    {escalation.assignedTo && (
                        <>
                            <p className="info">Name: {escalation.assignedTo?.name}</p>
                            <p className="info">Description: {escalation.assignedTo?.description}</p>
                        </>
                    )}
                    <p className="info">Priority: {escalation.escalationPriority}</p>
                </div>    
            </div>    
        </Stack>
    );
};
